/**
 * BranchCartReviewDialog: "Changes at <branch>" (branch operations, 4 Oct 2026).
 * ─────────────────────────────────────────────────────────────────────────────
 * Opened by hooks/useBranchCartCheck after the cart moved to another branch,
 * before Place order when a line changed, and when order create answers
 * 409 BRANCH_LINES_CHANGED. Each flagged line says what changed there:
 *
 *   Sold out at <branch>         [Remove]
 *   Not available at <branch>    [Remove]
 *   Not sold at <branch>         [Remove]
 *   Price at <branch>: X. Was Y. [Keep at new price] [Remove]
 *
 * Continue is enabled only when every line is resolved, so nobody pays for
 * something the branch cannot supply or at a price they did not see; until
 * then a line above it says what is left to do. Continue only closes the
 * dialog: the shopper is back where they were and places the order
 * themselves, as in the apps. The items total is the server's.
 *
 * Spread the hook's `dialogProps` onto it. Same copy as the apps'
 * BranchCartReviewSheet.
 * ─────────────────────────────────────────────────────────────────────────────
 */
import React from "react";
import styled from "styled-components";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { reviewHint, reviewTitle } from "../../utils/branchAvailability";

const INK = "#1F2937";
const MUTED = "#6B7280";
const HAIRLINE = "#E5E7EB";

/**
 * @param {object}   props
 * @param {object}   props.review      the hook's review: { branchName, items, subtotal, allResolved } or null
 * @param {Function} props.onRemove    (productId) => void
 * @param {Function} props.onKeep      (productId) => void
 * @param {Function} props.onClose     () => void
 * @param {Function} [props.onContinue] after Continue; defaults to closing
 * @param {Function} props.formatMoney (amount) => string
 */
const BranchCartReviewDialog = ({ review, onRemove, onKeep, onClose, onContinue, formatMoney }) => {
  const open = Boolean(review);
  const items = review?.items || [];
  const money = typeof formatMoney === "function" ? formatMoney : (n) => String(n);
  const hint = review && !review.allResolved ? reviewHint(items) : null;
  const finish = () => {
    if (typeof onClose === "function") onClose();
    if (typeof onContinue === "function") onContinue();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      scroll="paper"
      aria-labelledby="branch-review-title"
      PaperProps={{ sx: { borderRadius: "16px" } }}
    >
      <DialogTitle id="branch-review-title" sx={{ fontWeight: 700, color: INK }}>
        {reviewTitle(review?.branchName)}
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <List>
          {items.map((item) => (
            <Row key={item.productId}>
              <Copy>
                <Name $done={item.resolved === "removed"}>{item.name || "Item"}</Name>
                <Label>{item.label}</Label>
              </Copy>
              {item.resolved ? (
                <Done>{item.resolved === "removed" ? "Removed" : "Kept at new price"}</Done>
              ) : (
                <Actions>
                  {item.canKeep ? (
                    <Button
                      size="small"
                      variant="contained"
                      disableElevation
                      onClick={() => onKeep(item.productId)}
                      sx={{ textTransform: "none", background: "#083860", "&:hover": { background: "#0a4a7e" } }}
                    >
                      Keep at new price
                    </Button>
                  ) : null}
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => onRemove(item.productId)}
                    sx={{ textTransform: "none", color: "#B91C1C", borderColor: "#FCA5A5" }}
                  >
                    Remove
                  </Button>
                </Actions>
              )}
            </Row>
          ))}
        </List>
      </DialogContent>
      {hint ? <Hint role="status">{hint}</Hint> : null}
      <DialogActions sx={{ px: 3, py: 2, justifyContent: "space-between" }}>
        <Subtotal>
          {review?.subtotal !== null && review?.subtotal !== undefined
            ? `Items total: ${money(review.subtotal)}`
            : ""}
        </Subtotal>
        <Button
          variant="contained"
          disableElevation
          disabled={!review?.allResolved}
          onClick={finish}
          sx={{ textTransform: "none", background: "#083860", "&:hover": { background: "#0a4a7e" } }}
        >
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BranchCartReviewDialog;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const Row = styled.li`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 14px 24px;
  border-bottom: 1px solid ${HAIRLINE};
  &:last-child {
    border-bottom: none;
  }
`;

const Copy = styled.div`
  flex: 1 1 220px;
  min-width: 0;
`;

const Name = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${INK};
  text-decoration: ${(p) => (p.$done ? "line-through" : "none")};
`;

const Label = styled.div`
  margin-top: 2px;
  font-size: 13px;
  color: ${MUTED};
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  flex-shrink: 0;
`;

const Done = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #047857;
`;

const Subtotal = styled.span`
  font-size: 14px;
  color: ${INK};
`;

const Hint = styled.p`
  margin: 0;
  padding: 12px 24px 0;
  font-size: 14px;
  font-weight: 600;
  color: ${MUTED};
`;
